import React, { useRef, useState } from 'react'
// import './styles/app-message-audience-builder.scss'

import { DomainDto } from '../../../dtos/domain'
import { AudienceConfig, MutableAudienceConfig } from '@pushly/models/lib/structs/audience-config'
import { SegmentDto } from '../../../dtos/segment'
import { ComboSegmentSelectionType } from '@pushly/aqe/lib/components/combo-segment-selection/combo-segment-selection'
import { useLoadableDataRefState, useMountedRef, useService } from '@pushly/aqe/lib/hooks'
import { AppState } from '../../../stores/app'
import { BatchService } from '../../../services/batch'
import { canCreateSendToAll } from '../../notification-builder/helpers'
import { INestedBatchRequest } from '../../../interfaces/batch-requests'
import { convertCase } from '../../../_utils/utils'
import { generateUID } from '../../../_utils/uid'
import { SegmentSource } from '@pushly/models/lib/enums/segment-source'

import { ComboSegmentSelection, Drawer, Well } from '@pushly/aqe/lib/components'
import { useAppMessageBuilderContext } from '../../../features/app-messages/context'
import { getAvailableSegmentTypes, getEnabledDeliveryChannels } from '../../../_utils/domain'
import { HIDDEN_IN_CREATION_FLOWS, SHOW_FIRST_IN_DROPDOWN } from '../../../constants'
import { SegmentType } from '@pushly/models/lib/enums/segment-type'
import { SegmentQueryBuilder } from '../../segment-query-builder'
import type { SegmentQueryBuilderHandle } from '../../segment-query-builder'
import type { SegmentModel } from '../../../models/segments/segment.model'
import type { OrgSegmentModel } from '../../../models/segments/org-segment.model'
import { getClassNames } from '../../../_utils/classnames'
import { Radio, Select, Skeleton, Tooltip } from 'antd'

type AudienceBuilder = {
    className?: string
    domain: DomainDto
    disabled?: boolean
    loading?: boolean

    shouldSetDefaultIcon?: boolean

    hideExclusion?: boolean
    inclusionLabel?: string | false
}

interface IDependencies {
    allAppUsersSegment: SegmentDto | undefined
    defaultSegment: SegmentDto | undefined
    initialSegments: SegmentDto[]
    stickySegments: SegmentDto[]
    availableSegments: SegmentDto[]
}

interface IDrawerState {
    visible: boolean
    key?: string
    newSegmentType?: ComboSegmentSelectionType
    newSegmentName?: string
}

export const AppMessageAudienceBuilder = (props: AudienceBuilder) => {
    const [_mounted, runIfMounted] = useMountedRef()
    const { className, disabled, loading, domain, hideExclusion, inclusionLabel } = props

    const [context, dispatch] = useAppMessageBuilderContext()
    const value = context.variants[context.selectedVariantIdx].schedules![0].audience

    const queryBuilderRef = useRef<SegmentQueryBuilderHandle>(null)

    const appState = useService(AppState)
    const batchSvc = useService(BatchService)
    const [dependencies, setDependencies] = useLoadableDataRefState<IDependencies>({ loading: false })
    const [forceReloadDependencies, setForceReloadDependencies] = React.useState<boolean>(false)
    const [segmentDrawerState, setSegmentDrawerState] = useState<IDrawerState>({
        visible: false,
        key: undefined,
        newSegmentName: undefined,
        newSegmentType: undefined,
    })
    const [segmentSearchTerm, setSegmentSearchTerm] = React.useState<string | undefined>(undefined)
    const [exclusionsEnabled, setExclusionsEnabled] = useState(!!value.excludedSegmentIds?.length)

    const workingModel = value?.toMutable() ?? new MutableAudienceConfig()

    const canTargetAllSubs = canCreateSendToAll(domain, appState)
    const isAllSubsAudience =
        canTargetAllSubs &&
        !!dependencies.current.data?.defaultSegment &&
        workingModel.segmentIds?.[0]?.toString() === dependencies.current.data.defaultSegment.id?.toString()
    const isAllAppUserAudience =
        canTargetAllSubs &&
        !!dependencies.current.data?.allAppUsersSegment &&
        workingModel.segmentIds?.[0]?.toString() === dependencies.current.data.allAppUsersSegment.id?.toString()

    const onChange = (change: Partial<AudienceConfig>) => {
        dispatch({ type: 'patch', entity: 'audience', data: workingModel.clone({ ...change }) })
    }

    /**
     * Load all audience dependencies
     *
     * Includes:
     * - Default Segments - used within the audience type <Radio.Group />
     * - Standard Segments
     * - Adhoc or Inline (if part of initial segment selections)
     *
     * Initial segment selections should be loaded separately by ID to ensure
     * they are not filtered out during the all segments API call. They should
     * then be de-duped and added to the available options.
     */
    React.useEffect(() => {
        const loadDependencies = async () => {
            // ensure that available segments is not null and either
            // the default segment or initial segments are not null
            // before showing the component
            const hasLoadedDeps =
                !!dependencies.current.data?.availableSegments &&
                (!!dependencies.current.data?.defaultSegment || !!dependencies.current.data?.initialSegments)

            // 1. wait until parent is done loading (loading)
            // 2. ensure dependencies are not loaded or currently loading
            if (forceReloadDependencies || (!loading && !hasLoadedDeps && !dependencies.current.loading)) {
                setForceReloadDependencies(false)
                setDependencies({ ...dependencies, loading: true })

                const segmentsApiPath = `/domains/${domain.id}/segments`

                /**
                 * the base segment load should only include standard and the default segment
                 * non-standard segments are handled below in initialSegments
                 *
                 * fields must include:
                 * - id: value
                 * - name: label
                 * - source: required for filtering out adhoc
                 * - isDefault: required to set domain default id
                 * - iconUrl: required for ensuring default icon override when single segment is selected
                 */
                const batchReqs: INestedBatchRequest[] = [
                    {
                        method: 'get',
                        relative_path: `${segmentsApiPath}?include_default=1&include_treated=0&pagination=0&source=standard&fields=id,name,source,isDefault,iconUrl,computedStatus,flags,segment_type`,
                        meta: { all_segments_req: true },
                    },
                ]

                const initialSegmentIds = [...(value.segmentIds ?? []), ...(value.excludedSegmentIds ?? [])]
                if (initialSegmentIds.length) {
                    // initial segment load should always include "inline", "adhoc", "deleted"
                    // in addition to standard to ensure all segments are loaded when a user
                    // attempts to duplicate an existing notification
                    batchReqs.push({
                        method: 'get',
                        relative_path: `${segmentsApiPath}?id=${initialSegmentIds.join(
                            ',',
                        )}&include_deleted=true&source=standard,inline,adhoc`,
                        meta: { init_segments_req: true },
                    })
                }

                const res = await batchSvc.batch(
                    { requests: batchReqs },
                    {
                        cancellationKey: 'nab-isl',
                    },
                )

                runIfMounted(() => {
                    if (res.ok) {
                        let allAppUsersSegment: SegmentDto | undefined
                        let allSegments: SegmentDto[] = []
                        let defaultSegment: SegmentDto | undefined
                        let stickySegments: SegmentDto[] = []

                        const allSegmentsReq = res.data.find((r) => r.meta?.all_segments_req)
                        if (allSegmentsReq?.code === 200) {
                            // batch requests return string body that must be parsed and case-converted
                            const allSegmentsRaw = JSON.parse(allSegmentsReq.body)?.data ?? []
                            const preProcessAllSegments = convertCase(allSegmentsRaw, 'camel').map(SegmentDto.parse)

                            const allowedSegmentTypes = getAvailableSegmentTypes(domain)
                            // for each let's us loop once, rather than twice for each segment list type we want to build
                            preProcessAllSegments.forEach((s) => {
                                if (s.type === SegmentType.ALL_APP_USERS) {
                                    allAppUsersSegment = s
                                }

                                if (
                                    !s.flags.includes(HIDDEN_IN_CREATION_FLOWS) &&
                                    allowedSegmentTypes.includes(s.type)
                                ) {
                                    if (!s.flags.includes(SHOW_FIRST_IN_DROPDOWN)) {
                                        allSegments.push(s)
                                    }
                                }
                            })

                            defaultSegment = preProcessAllSegments.find(
                                (s) => s.type === SegmentType.ALL_PUSH_SUBSCRIBERS,
                            )
                            if (allAppUsersSegment) {
                                // if current user can target all subs and there are
                                // currently no initialized segment ids then set the
                                // default all segment value
                                if (canTargetAllSubs && initialSegmentIds.length === 0) {
                                    workingModel.segmentIds = [allAppUsersSegment.id]

                                    onChange?.(workingModel)
                                }
                            }
                        }

                        const initialSegments: SegmentDto[] = []
                        const initSegmentsReq = res.data.find((r) => r.meta?.init_segments_req)
                        if (initSegmentsReq?.code === 200) {
                            const rawSegments = JSON.parse(initSegmentsReq.body)?.data ?? []
                            if (rawSegments) {
                                convertCase(rawSegments, 'camel').forEach((rs) => {
                                    const segment = SegmentDto.parse(rs)
                                    initialSegments.push(segment)

                                    // re-attach initial segments into the all segment array
                                    if (!allSegments.find((s) => s.id === segment.id)) {
                                        allSegments.push(segment)
                                    }
                                })
                            }
                        }

                        setDependencies({
                            data: {
                                allAppUsersSegment,
                                defaultSegment,
                                initialSegments,
                                stickySegments,
                                availableSegments: allSegments,
                            },
                            loading: false,
                        })

                        dispatch({ type: 'patch', entity: 'available-segments', data: allSegments })
                    } else {
                        setDependencies({
                            data: {
                                allAppUsersSegment: undefined,
                                defaultSegment: undefined,
                                initialSegments: [],
                                stickySegments: [],
                                availableSegments: [],
                            },
                            loading: false,
                        })
                    }
                })
            }
        }

        loadDependencies().then()
    }, [loading, !!value.segmentIds, !!value.excludedSegmentIds, forceReloadDependencies])

    const handleNewSegmentClick = React.useCallback((type: ComboSegmentSelectionType, name: string | undefined) => {
        setSegmentDrawerState({
            ...segmentDrawerState,
            visible: true,
            key: generateUID(),
            newSegmentType: type,
            newSegmentName: name,
        })
        setSegmentSearchTerm(undefined)
    }, [])

    // The new builder reports its saved segment through onSaved rather than a submit() return,
    // so the id-append the legacy path did after `_submit()` lives here instead. It only fires on
    // a successful save, which is the same guard the legacy `segment?.id` check stood in for.
    const handleSegmentSaved = (segment: SegmentModel | OrgSegmentModel) => {
        const type = segmentDrawerState.newSegmentType

        closeDrawer()
        const audienceValue = workingModel.clone()

        if (type === 'included') {
            const segmentIds = audienceValue.segmentIds ?? []
            segmentIds.push(segment.getId())
            audienceValue.segmentIds = segmentIds
        } else {
            const segmentIds = audienceValue.excludedSegmentIds ?? []
            segmentIds.push(segment.getId())
            audienceValue.excludedSegmentIds = segmentIds
        }

        onChange?.(audienceValue)
        setForceReloadDependencies(true)
    }

    const closeDrawer = () => {
        setSegmentDrawerState({
            ...segmentDrawerState,
            visible: false,
            key: undefined,
            newSegmentType: undefined,
            newSegmentName: undefined,
        })
    }

    const renderNewSegmentOption = React.useCallback(
        (type: ComboSegmentSelectionType, isEmptyMode: boolean) => {
            return (
                <div
                    className={getClassNames(null, 'new-segment', {
                        [`type-${type}`]: true,
                        ['no-data']: isEmptyMode,
                    })}
                    onClick={() => handleNewSegmentClick(type, segmentSearchTerm)}
                >
                    Create New Segment
                </div>
            )
        },
        [segmentSearchTerm],
    )

    const extractSelectOptionProps = React.useCallback(
        (segment: SegmentDto) => ({
            value: segment.id,
            label: segment.name,
            title: segment.name,
        }),
        [],
    )

    const isLoading = loading || dependencies.current.loading
    const availableSegments = dependencies.current.data?.availableSegments ?? []

    /**
     * Visible Segments cannot be:
     * 1. isDefault
     * 2. source=adhoc unless they are selected in either included or excluded
     */
    const visibleSegments = availableSegments.filter((segment) => {
        return (
            !segment.isDefault &&
            (segment.source !== SegmentSource.ADHOC ||
                workingModel.segmentIds?.includes(segment.id) ||
                workingModel.excludedSegmentIds?.includes(segment.id))
        )
    })

    const AllSubscribersWrapper = canTargetAllSubs ? (_: any) => _.children : Tooltip

    return (
        <Well
            className={getClassNames('notification-audience-builder', className, {
                'hide-exclusions': hideExclusion,
            })}
            title="Audience"
            hideFooter={true}
        >
            <Skeleton loading={isLoading} active={true} avatar={false} title={false}>
                {isLoading ? (
                    <></>
                ) : (
                    <>
                        <Radio.Group
                            className="audience-builder-type-radio"
                            size="small"
                            buttonStyle="solid"
                            disabled={disabled || context.disabled}
                            value={
                                !canTargetAllSubs
                                    ? 'segments'
                                    : isAllSubsAudience
                                    ? 'all'
                                    : isAllAppUserAudience
                                    ? 'app-users'
                                    : 'segments'
                            }
                            onChange={(ev) => {
                                const v = ev.target.value
                                let segmentIds
                                if (v === 'app-users') {
                                    if (!!dependencies.current.data?.allAppUsersSegment) {
                                        segmentIds = [dependencies.current.data?.allAppUsersSegment?.id]
                                    }
                                } else if (v !== 'all') {
                                    segmentIds = []
                                } else if (!!dependencies.current.data?.defaultSegment) {
                                    segmentIds = [dependencies.current.data.defaultSegment.id]
                                }

                                workingModel.segmentIds = segmentIds

                                onChange?.(workingModel)
                            }}
                        >
                            <AllSubscribersWrapper title="Targeting all subscribers has been disabled on this domain">
                                <Radio.Button value="app-users" disabled={!canTargetAllSubs}>
                                    All App Users
                                </Radio.Button>
                                <Radio.Button value="all" disabled={!canTargetAllSubs}>
                                    All Push Subscribers
                                </Radio.Button>
                            </AllSubscribersWrapper>
                            <Radio.Button value="segments">Specific Segments</Radio.Button>
                        </Radio.Group>

                        <ComboSegmentSelection
                            switchSize="small"
                            disabled={disabled || context.disabled}
                            hideIncluded={canTargetAllSubs && (isAllSubsAudience || isAllAppUserAudience)}
                            includedLabel={inclusionLabel}
                            hideExcluded={hideExclusion}
                            value={{
                                allSubscribers: isAllSubsAudience,
                                segmentIds: workingModel.segmentIds,
                                excludedSegmentIds: exclusionsEnabled ? workingModel.excludedSegmentIds : undefined,
                            }}
                            options={visibleSegments.map(extractSelectOptionProps)}
                            notFoundContent={(type) => renderNewSegmentOption(type, true)}
                            stickyOptionRenderer={(type) => (
                                <Select.Option
                                    key="new"
                                    value="new"
                                    className={getClassNames(null, 'new-segment-option')}
                                >
                                    {renderNewSegmentOption(type, false)}
                                </Select.Option>
                            )}
                            onChange={(v) => {
                                setExclusionsEnabled(!!v.excludedSegmentIds)

                                workingModel.segmentIds = v.segmentIds ?? []
                                workingModel.excludedSegmentIds = v.excludedSegmentIds ?? []

                                onChange?.(workingModel)
                            }}
                            onSearch={(v) => setSegmentSearchTerm(v)}
                        />
                    </>
                )}
            </Skeleton>

            <Drawer
                className={getClassNames('combo-segment-select-new-segment-drawer')}
                title="Create New Segment"
                open={segmentDrawerState.visible}
                onSubmit={() => queryBuilderRef.current?.submit()}
                onClose={closeDrawer}
            >
                <SegmentQueryBuilder
                    key={segmentDrawerState.key ?? 'unknown'}
                    ref={queryBuilderRef}
                    level="domain"
                    domainId={appState.currentDomain!.id}
                    mode="drawer"
                    name={segmentDrawerState.newSegmentName}
                    source={SegmentSource.STANDARD}
                    channels={getEnabledDeliveryChannels(domain)}
                    hideChannelsSelector={true}
                    onSaved={handleSegmentSaved}
                />
            </Drawer>
        </Well>
    )
}
